import { useMemo, useState } from 'react'
import { CostMatrixPanel } from '../components/CostMatrixPanel'
import { PpdPanel } from '../components/PpdPanel'
import { useNavigate, useParams } from 'react-router-dom'
import { ApiError, api } from '../api/client'
import {
  useAllocationRun,
  useRunMatrix,
  useRunResultsByCostCenter,
  useRunRollup,
  useRunSnapshot,
} from '../api/hooks'
import type { ResultByCostCenter, ResultMatrixRow, ResultRollup } from '../api/types'
import { formatMoney, formatPercent } from '../components/Badges'
import { formatDate, formatDateTime } from '../components/formatTime'

/**
 * Screen 9 — Results Explorer (§10).
 *
 * Account × cost center matrix, a rollup toggle between what was posted and what rolls
 * up the hierarchy, and a drill from any cell to the contributing driver and its
 * resolved percentages. The run's snapshot sits alongside so an analyst can answer
 * "why did Collections get this number" without leaving the screen.
 */
export function ResultsExplorer() {
  const { runId: runIdParam } = useParams<{ runId: string }>()
  const runId = runIdParam ? Number(runIdParam) : null
  const navigate = useNavigate()

  const [rollup, setRollup] = useState(false)
  const [selectedCell, setSelectedCell] = useState<{ accountId: number; costCenterId: number } | null>(null)
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)

  async function download(format: 'csv' | 'xlsx') {
    setExporting(format)
    setExportError(null)

    try {
      await api.download(`/api/allocation-runs/${runId}/export?format=${format}`)
    } catch (e) {
      // The most likely refusal is a matrix too large for a worksheet, and it names the
      // row count and suggests CSV — so show what the server said rather than "failed".
      setExportError(e instanceof ApiError ? e.message : 'The export could not be produced.')
    } finally {
      setExporting(null)
    }
  }

  const { data: run } = useAllocationRun(runId)
  const { data: byCostCenter = [] } = useRunResultsByCostCenter(runId)
  const { data: rolledUp = [] } = useRunRollup(runId, rollup)
  const { data: matrix = [] } = useRunMatrix(runId)
  const { data: snapshot = [] } = useRunSnapshot(runId)

  /** Matrix cells keyed by account × cost center, summing the drivers that contributed. */
  const cells = useMemo(() => {
    const map = new Map<string, { amount: number; rows: ResultMatrixRow[] }>()
    for (const row of matrix) {
      const key = `${row.accountId}:${row.costCenterId}`
      const existing = map.get(key)
      if (existing) {
        existing.amount += row.allocatedAmount
        existing.rows.push(row)
      } else {
        map.set(key, { amount: row.allocatedAmount, rows: [row] })
      }
    }
    return map
  }, [matrix])

  const accounts = useMemo(() => {
    const seen = new Map<number, { id: number; number: string; name: string; source: number }>()
    for (const row of matrix) {
      if (!seen.has(row.accountId)) {
        seen.set(row.accountId, {
          id: row.accountId,
          number: row.accountNumber,
          name: row.accountName,
          source: row.sourceAmount,
        })
      }
    }
    return [...seen.values()].sort((a, b) => a.number.localeCompare(b.number))
  }, [matrix])

  const costCenters = useMemo(() => {
    const seen = new Map<number, { id: number; code: string; name: string; depth: number }>()
    for (const row of matrix) {
      if (!seen.has(row.costCenterId)) {
        seen.set(row.costCenterId, {
          id: row.costCenterId,
          code: row.costCenterCode,
          name: row.costCenterName,
          depth: row.depth,
        })
      }
    }
    return [...seen.values()]
  }, [matrix])

  const selectedRows = selectedCell
    ? (cells.get(`${selectedCell.accountId}:${selectedCell.costCenterId}`)?.rows ?? [])
    : []

  if (!runId) {
    return (
      <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        Choose a run from the Run Console.
      </p>
    )
  }

  return (
    <div className="flex h-full">
      <section className="flex min-w-0 flex-1 flex-col">
        <div
          className="flex flex-wrap items-center gap-3 border-b px-4 py-2"
          style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
        >
          <button
            className="text-[12px] underline"
            style={{ color: 'var(--color-accent)' }}
            onClick={() => navigate('/runs')}
          >
            ← Runs
          </button>
          <h1 className="text-[14px] font-semibold">Run {runId}</h1>
          {run && (
            <span className="text-[12px]" style={{ color: 'var(--color-muted)' }}>
              {run.periodCode} · {run.status}
              {run.isPreview && ' · preview'}
            </span>
          )}

          <label className="flex items-center gap-1.5 text-[12px]">
            <input type="checkbox" checked={rollup} onChange={(e) => setRollup(e.target.checked)} />
            Roll up the hierarchy
          </label>

          <button
            className="btn btn-sm ml-auto"
            onClick={() => void download('csv')}
            disabled={exporting !== null}
          >
            {exporting === 'csv' ? 'Exporting…' : 'Export CSV'}
          </button>

          <button
            className="btn btn-sm"
            onClick={() => void download('xlsx')}
            disabled={exporting !== null}
            title="Three sheets: the allocation, the cost center rollup, and the driver snapshot the run used."
          >
            {exporting === 'xlsx' ? 'Exporting…' : 'Export XLSX'}
          </button>
        </div>

        {exportError && (
          <div
            className="border-b px-4 py-2 text-[12px]"
            style={{
              background: 'var(--color-error-wash)',
              color: 'var(--color-error)',
              borderColor: 'var(--color-rule-soft)',
            }}
            role="alert"
          >
            {exportError}
          </div>
        )}

        {/* A purged run has a header and no rows. Without this the results screen looks
            exactly like a run that allocated nothing, which is a very different thing. */}
        {run?.resultsPurged && (
          <div
            className="border-b px-4 py-2 text-[12px]"
            style={{
              background: 'var(--color-panel-alt)',
              color: 'var(--color-muted)',
              borderColor: 'var(--color-rule-soft)',
            }}
          >
            The detailed results for this run were removed by retention on{' '}
            {formatDate(run.resultsPurgedUtc)}. Its totals and
            reconciliation are unchanged and shown above.
          </div>
        )}

        {run && (
          <div
            className="flex shrink-0 items-center gap-5 border-b px-4 py-1.5 text-[12px]"
            style={{
              background: run.reconciles ? 'var(--color-ok-wash)' : 'var(--color-panel-alt)',
              borderColor: 'var(--color-rule-soft)',
              color: run.reconciles ? 'var(--color-ok)' : 'var(--color-muted)',
            }}
          >
            <span>
              source <span className="num">{formatMoney(run.totalSourceAmount ?? 0)}</span>
            </span>
            <span>
              allocated <span className="num">{formatMoney(run.totalAllocatedAmount ?? 0)}</span>
            </span>
            {run.reconciles && <span>✓ reconciled</span>}
            <span className="ml-auto" style={{ color: 'var(--color-faint)' }}>
              {/*
                §4.6: rollup views read the CURRENT hierarchy, so a historical run's
                rolled-up totals can shift after a reparent while its direct postings
                stay fixed. Surfacing the run's date is what stops that reading as a bug.
              */}
              posted {formatDateTime(run.completedUtc)} ·
              rollup reflects the hierarchy as it is now
            </span>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto" style={{ background: 'var(--color-panel)' }}>
          {rollup ? (
            <RollupTable rows={rolledUp} />
          ) : (
            <MatrixTable
              accounts={accounts}
              costCenters={costCenters}
              cells={cells}
              byCostCenter={byCostCenter}
              selectedCell={selectedCell}
              onSelect={setSelectedCell}
            />
          )}

          {/*
              Below the grid rather than beside it. Both are readings OF the run the grid
              shows, and putting them in the sidebar would compete with the drill-down that
              is already there.
          */}
          <CostMatrixPanel runId={runId} />
          <PpdPanel runId={runId} />
        </div>
      </section>

      <aside
        className="w-96 shrink-0 overflow-auto border-l"
        style={{ background: 'var(--color-panel)', borderColor: 'var(--color-rule-soft)' }}
      >
        {selectedRows.length > 0 ? (
          <div className="p-3">
            <h2 className="mb-1 text-[13px] font-semibold">
              {selectedRows[0]!.accountName} → {selectedRows[0]!.costCenterName}
            </h2>
            <p className="mb-3 text-[11px]" style={{ color: 'var(--color-faint)' }}>
              How this number was produced.
            </p>

            {selectedRows.map((row) => (
              <div
                key={`${row.costDriverId}`}
                className="mb-2 rounded border p-2"
                style={{ borderColor: 'var(--color-rule-soft)' }}
              >
                <div className="mb-1 text-[12px] font-medium">{row.costDriverName}</div>
                <dl
                  className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px]"
                  style={{ color: 'var(--color-muted)' }}
                >
                  <dt>Account balance</dt>
                  <dd className="num">{formatMoney(row.sourceAmount)}</dd>
                  <dt>Routed through driver</dt>
                  <dd className="num">{formatPercent(row.driverSharePercent)}</dd>
                  <dt>Driver share here</dt>
                  <dd className="num">{formatPercent(row.driverPercent)}</dd>
                  <dt style={{ color: 'var(--color-ink)' }}>Allocated</dt>
                  <dd className="num" style={{ color: 'var(--color-ink)', fontWeight: 600 }}>
                    {formatMoney(row.allocatedAmount)}
                  </dd>
                </dl>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-3">
            <h2 className="mb-1 text-[13px] font-semibold">Driver snapshot</h2>
            <p className="mb-3 text-[11px]" style={{ color: 'var(--color-faint)' }}>
              The percentages this run actually used. Stored with the run, so editing a driver
              afterwards cannot change what it says it did.
            </p>

            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule-soft)' }}>
                  <th className="col-head py-1">Driver</th>
                  <th className="col-head py-1">Center</th>
                  <th className="col-head py-1 text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.map((s) => (
                  <tr
                    key={`${s.costDriverId}-${s.costCenterId}`}
                    className="border-b"
                    style={{ borderColor: 'var(--color-rule-soft)' }}
                  >
                    <td className="py-1 text-[11px]">{s.costDriverName}</td>
                    <td className="py-1 text-[11px]" style={{ color: 'var(--color-muted)' }}>
                      {s.costCenterCode}
                    </td>
                    <td className="num py-1 text-[11px]">{formatPercent(s.resolvedPercent)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </aside>
    </div>
  )
}

function MatrixTable({
  accounts,
  costCenters,
  cells,
  byCostCenter,
  selectedCell,
  onSelect,
}: {
  accounts: { id: number; number: string; name: string; source: number }[]
  costCenters: { id: number; code: string; name: string; depth: number }[]
  cells: Map<string, { amount: number; rows: ResultMatrixRow[] }>
  byCostCenter: ResultByCostCenter[]
  selectedCell: { accountId: number; costCenterId: number } | null
  onSelect: (cell: { accountId: number; costCenterId: number }) => void
}) {
  // Column order follows the by-cost-center result, which is already path-ordered.
  const ordered = byCostCenter.length > 0
    ? byCostCenter
        .map((r) => costCenters.find((c) => c.id === r.costCenterId))
        .filter((c): c is NonNullable<typeof c> => c !== undefined)
    : costCenters

  const columnTotals = new Map<number, number>()
  for (const [key, cell] of cells) {
    const costCenterId = Number(key.split(':')[1])
    columnTotals.set(costCenterId, (columnTotals.get(costCenterId) ?? 0) + cell.amount)
  }

  return (
    <table className="border-collapse">
      <thead className="sticky top-0 z-10" style={{ background: 'var(--color-panel-alt)' }}>
        <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
          <th className="col-head sticky left-0 px-3 py-1.5" style={{ background: 'var(--color-panel-alt)' }}>
            Account
          </th>
          <th className="col-head px-3 py-1.5 text-right">Source</th>
          {ordered.map((cc) => (
            <th key={cc.id} className="col-head px-3 py-1.5 text-right whitespace-nowrap" title={cc.name}>
              {cc.code}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {accounts.map((account) => (
          <tr key={account.id} className="grid-row border-b" style={{ borderColor: 'var(--color-rule-soft)' }}>
            <td
              className="sticky left-0 px-3 py-1"
              style={{ background: 'var(--color-panel)' }}
              title={account.name}
            >
              <span className="ident">{account.number}</span>
            </td>
            <td className="num px-3 py-1" style={{ color: 'var(--color-muted)' }}>
              {formatMoney(account.source)}
            </td>
            {ordered.map((cc) => {
              const cell = cells.get(`${account.id}:${cc.id}`)
              const isSelected =
                selectedCell?.accountId === account.id && selectedCell?.costCenterId === cc.id

              return (
                <td
                  key={cc.id}
                  className="num px-3 py-1"
                  style={{
                    cursor: cell ? 'pointer' : 'default',
                    background: isSelected ? 'var(--color-accent-wash)' : undefined,
                    color: cell ? undefined : 'var(--color-faint)',
                  }}
                  onClick={() => cell && onSelect({ accountId: account.id, costCenterId: cc.id })}
                >
                  {cell ? formatMoney(cell.amount) : '—'}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
      <tfoot className="sticky bottom-0" style={{ background: 'var(--color-panel-alt)' }}>
        <tr className="border-t" style={{ borderColor: 'var(--color-rule)' }}>
          <td className="col-head sticky left-0 px-3 py-1.5" style={{ background: 'var(--color-panel-alt)' }}>
            total
          </td>
          <td className="num px-3 py-1.5 font-semibold">
            {formatMoney(accounts.reduce((s, a) => s + a.source, 0))}
          </td>
          {ordered.map((cc) => (
            <td key={cc.id} className="num px-3 py-1.5 font-semibold">
              {formatMoney(columnTotals.get(cc.id) ?? 0)}
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  )
}

/**
 * The rollup, with Direct / Descendant / RolledUp kept apart.
 *
 * A single blended number would leave an analyst unable to tell whether Operations'
 * $9,000 is its own cost, its children's, or a mix — and since a parent can carry its
 * own direct allocation, it is routinely a mix.
 */
function RollupTable({ rows }: { rows: ResultRollup[] }) {
  return (
    <table className="w-full border-collapse">
      <thead className="sticky top-0 z-10" style={{ background: 'var(--color-panel-alt)' }}>
        <tr className="border-b text-left" style={{ borderColor: 'var(--color-rule)' }}>
          <th className="col-head px-3 py-1.5">Cost center</th>
          <th className="col-head px-3 py-1.5">Code</th>
          <th className="col-head px-3 py-1.5 text-right">Direct</th>
          <th className="col-head px-3 py-1.5 text-right">Descendant</th>
          <th className="col-head px-3 py-1.5 text-right">Rolled up</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.costCenterId} className="grid-row border-b" style={{ borderColor: 'var(--color-rule-soft)' }}>
            <td className="px-3 py-1">
              <span style={{ paddingLeft: `${row.depth * 16}px` }}>{row.costCenterName}</span>
            </td>
            <td className="ident px-3 py-1" style={{ color: 'var(--color-muted)' }}>
              {row.costCenterCode}
            </td>
            <td
              className="num px-3 py-1"
              title="Posted to this cost center itself."
              style={{ color: row.directAmount === 0 ? 'var(--color-faint)' : undefined }}
            >
              {formatMoney(row.directAmount)}
            </td>
            <td
              className="num px-3 py-1"
              title="Posted to any descendant."
              style={{ color: row.descendantAmount === 0 ? 'var(--color-faint)' : undefined }}
            >
              {formatMoney(row.descendantAmount)}
            </td>
            <td className="num px-3 py-1 font-semibold">{formatMoney(row.rolledUpAmount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
